import { useState } from 'react';

import clsx from 'clsx';
import { useFlags } from 'flagsmith/react';

// Utils
import useFormatDateTime from '@/hooks/format/useFormatDateTime';
import useNetworkOauthUrls from '@/utils/hooks/signup/useNetworkOauthUrls';
import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';
import type {
    MutateUserFBPermissions,
    PermissionType,
    UserFBPermissionsType
} from '@/utils/hooks/swr/useUserFBPermissions';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useAcl from '@/utils/hooks/user/useAcl';

import { post } from '@/utils/client';
import faqUrl from '@/utils/urls/faqUrl';
import useToggle from '@/hooks/utils/useToggle';

import type { SocialProfileType } from '@/types/socialProfileType';
import type { UserType } from '@/types/userType';

// Lib
import { aclFeatures } from '@/lib/acl/aclFeatures';
import pageRights from '@/lib/acl/pageRights';
import { MANAGE_DM_TRACKER } from '@/lib/routes/privateRoutes';
import { FACEBOOK } from '@/lib/socialProfile';
import { PENDO_CLASS_DATA_ACCESS_REFRESH_ACCESS } from '@/lib/tools/pendo/class';
import { url_faq_data_access_current_fb_profile_connected } from '@/lib/urls/faqUrls';

// Components
import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import LinkBase from '@/components/base/LinkBase';
import Tooltip from '@/components/base/Tooltip';
import { UpsellCrownIcon } from '@/components/composite/Upsell/UpsellCrownIcon/UpsellCrownIcon';
import { DataAccessLogoutModal } from '@/components/pages/settings/modals/DataAccessLogoutModal';
import { Trans } from '@/components/tools/translation';

// Routes
import { SETTINGS_DATA_ACCESS_LOGOUT } from '@/routes/apiEndpoints';

type DataAccessFormProps = {
    user: UserType;
    userFbPermissions: UserFBPermissionsType;
    mutate: MutateUserFBPermissions;
};

const DataAccessForm = ({ user, userFbPermissions, mutate }: DataAccessFormProps) => {
    const { t } = useTranslation('settings');
    const { hasAcl } = useAcl();
    const { formatDateTime } = useFormatDateTime(
        user?.settings?.timezone,
        user?.settings?.platform_language
    );
    const { getOauthUrl } = useNetworkOauthUrls();
    const { isOpen: isOpenLogoutModal, toggle: toggleLogoutModal } = useToggle();

    const flags = useFlags(['facebook_dm']);
    const hasMessengerDM = flags?.facebook_dm?.enabled;

    const [logoutLoading, setLogoutLoading] = useState(false);

    const { subscription } = useSubscription();

    const isDMLocked = !pageRights[MANAGE_DM_TRACKER]?.plans?.includes(subscription?.product_key);

    const metaPermissions: Partial<
        Record<
            PermissionType,
            { title: string; icon: string; isHidden?: boolean; isLocked?: boolean }
        >
    > = {
        scheduler: { title: 'data_access_page.automatic_publishing', icon: 'calendar' },
        analytics: { title: 'data_access_page.analytics_insights', icon: 'stats' },
        ads: {
            title: 'data_access_page.promoted_post_analytics',
            icon: 'paid-data',
            isHidden: !hasAcl(aclFeatures.analytic.promoted_post.read)
        },
        instagram_dm: {
            title: 'data_access_page.instagram_dms',
            icon: 'dm',
            isLocked: isDMLocked
        },
        messenger_dm: {
            title: 'data_access_page.messenger_dms',
            icon: 'dm',
            isLocked: isDMLocked,
            isHidden: !hasMessengerDM
        }
    } as const;

    const unlockPermission = ({
        query,
        permissions
    }: {
        query?: { auth_type: SocialProfileType['auth_status']['params']['auth_type'] };
        permissions?: PermissionType[];
    } = {}) => {
        window.location.assign(getOauthUrl({ network: FACEBOOK, query, permissions }));
    };

    const isDMPermission = (permission: PermissionType) =>
        permission === 'instagram_dm' || permission === 'messenger_dm';

    const handleUnlockPermission = (permission: PermissionType) => {
        if (metaPermissions[permission].isLocked) return;

        unlockPermission({ permissions: isDMPermission(permission) ? [permission] : [] });
    };

    return (
        <>
            <p className="font-GMedium text-sm leading-5">
                {userFbPermissions?.refresh_date ? (
                    <Trans
                        i18nKey="settings:data_access_page.data_access_desc_connected"
                        values={{
                            date: formatDateTime(
                                userFbPermissions.refresh_date,
                                'day_month_year_split'
                            )
                        }}
                        components={{ span: <span className="font-GSemiBold text-icoBlue" /> }}
                    />
                ) : (
                    t('data_access_page.data_access_desc_not_connected')
                )}
            </p>
            <p className="pt-2.5 font-GMedium text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                {t('data_access_page.data_access_notice_refresh')}
            </p>
            <div className="mt-8 xs:flex xs:items-center xs:justify-between">
                <div className="flex items-center">
                    <Icon
                        icon="facebook"
                        className="flex-none text-xl text-facebook dark:text-white"
                        aria-label="facebook-icon"
                    />
                    <p className="leading-0 ml-3 mr-2 text-sm">
                        {t('data_access_page.current_facebook_profile_connected')}
                        <Tooltip
                            content={
                                <Trans
                                    i18nKey={`settings:data_access_page.current_facebook_profile_connected_tooltip`}
                                    components={{
                                        hereLink: (
                                            <LinkBase
                                                key="link"
                                                href={faqUrl(
                                                    url_faq_data_access_current_fb_profile_connected
                                                )}
                                                className="underline"
                                                target="_blank"
                                                rel="help noopener noreferrer"
                                            />
                                        )
                                    }}
                                />
                            }
                            placement="top"
                            interactive={true}
                            popperOptions={{
                                // Prevent tooltip from popping in the wrong place
                                strategy: 'fixed'
                            }}>
                            <Icon
                                aria-label={t('commons:infos')}
                                icon="help"
                                className="ml-2 text-base text-icoGray-400 hover:text-icoGray-500 dark:text-icoDarkMode-wolf"
                            />
                        </Tooltip>
                    </p>
                </div>
                <Button
                    size="md"
                    variant="primary"
                    className={clsx(
                        'mt-5 w-full xs:mt-0 xs:w-0',
                        PENDO_CLASS_DATA_ACCESS_REFRESH_ACCESS
                    )}
                    onClick={() => unlockPermission({ query: { auth_type: 'reauthenticate' } })}>
                    {t('data_access_page.refresh_access')}
                </Button>
            </div>
            <div className="relative mt-9 rounded-md bg-icoGray-100 p-5 dark:bg-icoDarkMode-deepBlack md:flex md:items-start md:justify-between">
                <div className="-mt-10">
                    <Avatar
                        fallback="?"
                        size="xm"
                        url={userFbPermissions?.user?.picture?.data?.url ?? null}
                    />
                    <div className="mt-2">
                        <p>
                            {userFbPermissions?.user?.name
                                ? userFbPermissions?.user?.name
                                : t('data_access_page.no_name')}
                        </p>
                        <p className="mt-1 font-GMedium text-sm text-icoGray-500 dark:text-icoDarkMode-wolf">
                            {userFbPermissions?.user?.email
                                ? userFbPermissions?.user?.email
                                : t('data_access_page.no_email')}
                        </p>
                    </div>
                </div>
                {userFbPermissions?.user?.name && (
                    <div className="mt-8 flex items-center md:ml-4 md:mt-0">
                        <p className="font-GMedium text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                            {t('data_access_page.not_you')}{' '}
                        </p>
                        <button
                            className="ml-1 font-GMedium text-xs text-icoBlue underline"
                            onClick={() => toggleLogoutModal()}>
                            {t('data_access_page.logout')}
                        </button>
                        <Tooltip
                            content={t('data_access_page.logout_help')}
                            placement="top"
                            interactive={true}
                            popperOptions={{
                                //prevent tooltip to pop in the wrong place
                                strategy: 'fixed'
                            }}>
                            <Icon
                                aria-label={t('commons:infos')}
                                icon="help"
                                className="leading-0 ml-2 text-xs text-icoGray-400 dark:text-icoDarkMode-wolf"
                            />
                        </Tooltip>
                    </div>
                )}
            </div>
            <div className="mt-3.5 rounded-b rounded-t border-l border-r border-t border-icoGray-300 dark:border-icoDarkMode-beton">
                {Object.entries(metaPermissions).map(function ([key, permission]) {
                    if (permission.isHidden) {
                        return null;
                    }

                    return (
                        <div
                            className="flex items-center justify-between border-b border-icoGray-300 px-8 py-5 dark:border-icoDarkMode-beton"
                            key={permission.title}>
                            <div className="flex items-center gap-3">
                                <Icon icon={permission.icon} className="text-base text-icoBlue" />

                                <p className="font-GSemiBold text-sm">{t(permission.title)}</p>

                                {permission.isLocked && <UpsellCrownIcon />}
                            </div>
                            <div>
                                {userFbPermissions?.permissions &&
                                !permission.isLocked &&
                                Object.values(userFbPermissions.permissions).indexOf(key) !== -1 ? (
                                    <div className="flex py-2">
                                        <Icon
                                            icon="check"
                                            className="leading-0 text-sm text-icoBlue"
                                        />
                                        <p className="ml-1 text-xs text-icoBlue">
                                            {t('data_access_page.unlocked')}
                                        </p>
                                    </div>
                                ) : (
                                    <Button
                                        size="sm"
                                        variant="secondary"
                                        onClick={() => handleUnlockPermission(key)}
                                        disabled={permission.isLocked}>
                                        {t('data_access_page.unlock_permission')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            <DataAccessLogoutModal
                isOpen={isOpenLogoutModal}
                toggle={toggleLogoutModal}
                logoutLoading={logoutLoading}
                logout={() => {
                    setLogoutLoading(true);
                    post(SETTINGS_DATA_ACCESS_LOGOUT, {}).then(() => {
                        mutate();
                        toggleLogoutModal();
                        setLogoutLoading(false);
                    });
                }}
            />
        </>
    );
};

export default DataAccessForm;
